import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/notification';
import { formatDistanceToNow } from 'date-fns';
import Avatar from '../UI/Avatar';
import IconSVG from '../UI/IconSVG';
import {
  handleNotificationClick,
  getNotificationIcon,
  getNotificationTitle,
  getNotificationBody
} from '../../utils/notification-navigation';
import { useAuth } from '../../hooks';
import { auth } from '../../services/firebase/core';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Extract the needed properties
  const { id, type, dateCreated, read } = notification;
  
  // Get title and body using helpers
  const title = getNotificationTitle(notification);
  const body = getNotificationBody(notification);

  // Format the time distance - use timestamp (legacy) or dateCreated
  const timeAgo = formatDistanceToNow(
    (notification as any).timestamp || dateCreated || Date.now(), 
    { addSuffix: true }
  );

  // Get the notification background color based on read status and type
  const getBackgroundColor = () => {
    // Check both read and isRead (legacy) fields
    const isUnread = !read && !(notification as any).isRead;
    if (isUnread) {
      return 'bg-blue-50 dark:bg-blue-900/20';
    }
    return 'bg-white dark:bg-gray-800';
  };

  // Get the user photo if available
  const getUserPhoto = () => {
    if ('senderPhotoURL' in notification && notification.senderPhotoURL) {
      return notification.senderPhotoURL;
    }
    // For legacy notifications, we need to look up the user profile separately
    return undefined;
  };

  // Handle notification click
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use Redux user.uid first, fallback to Firebase auth.currentUser.uid
    const currentUserId = user?.uid || auth.currentUser?.uid;
    handleNotificationClick(notification, navigate, onMarkAsRead, currentUserId);
  };

  // Handle notification delete
  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(id);
  };

  const icon = getNotificationIcon(type);
  const bgColor = getBackgroundColor();
  const userPhoto = getUserPhoto();

  return (
    <div
      className={`block border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer select-none ${bgColor}`}
      onClick={handleClick}
      style={{ WebkitTapHighlightColor: 'transparent', WebkitTouchCallout: 'none' }}
    >
      <div className="flex p-4">
        {/* Icon or user avatar */}
        <div className="flex-shrink-0 mr-4">
          {userPhoto ? (
            <Avatar src={userPhoto} alt="User" size="md" />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              notification.isSysGen 
                ? 'bg-purple-100 dark:bg-purple-800'
                : notification.isAdditional
                ? 'bg-green-100 dark:bg-green-800'
                : 'bg-blue-100 dark:bg-blue-800'
            }`}>
              <IconSVG 
                icon={icon} 
                className={
                  notification.isSysGen 
                    ? 'text-purple-500 dark:text-purple-400'
                    : notification.isAdditional
                    ? 'text-green-500 dark:text-green-400'
                    : 'text-blue-500 dark:text-blue-400'
                } 
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {title || (notification as any).message || 'Notification'}
            </h4>
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
              {timeAgo}
            </span>
          </div>
          {body && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
              {body}
            </p>
          )}
          
          {/* Notification type badge */}
          {(notification.isSysGen || notification.isAdditional) && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                notification.isSysGen
                  ? 'bg-purple-600 text-white dark:bg-purple-500 dark:text-gray-900'
                  : 'bg-green-600 text-white dark:bg-green-500 dark:text-gray-900'
              }`}>
                {notification.isSysGen ? 'System' : 'Announcement'}
              </span>
            </div>
          )}
        </div>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          className="ml-4 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          aria-label="Delete notification"
        >
          <IconSVG 
            icon="close" 
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
          />
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;